import { ArrowUpRightOnBox, CheckCircleSolid, DocumentText } from "@medusajs/icons"
import { MessageBox } from "../components/message-box"
import { siteMessage, type SiteMessageKind } from "../lib/site-message"
import { Badge, Button, Checkbox, clx, Container, Heading, Input, Label, Skeleton, Text, toast, usePrompt } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState, type FormEvent, type ReactNode } from "react"
import { Link, useParams } from "react-router-dom"
import { BookDeliveryPanel } from "../components/order-booking"
import { DeliveryCharge } from "../components/delivery-charge"
import { RemoveItem } from "../components/remove-item"
import { AddOrderItem } from "../components/add-order-item"
import { BoxNowCopy } from "../components/boxnow-copy"
import { OrderNotes } from "../components/order-notes"
import { OrderEmails } from "../components/order-emails"
import { RefundForm, RefundPanel } from "../components/refund-panel"
import { ExchangeSend, ReturnForm, ReturnPanel } from "../components/return-panel"
import { DeliveryBadge, FulfilmentBadge, PaymentBadge } from "../components/order-badges"
import { PageHeader } from "../components/page"
import { CopyButton } from "../components/ui"
import { dateTime, DELIVERY_LABEL, money } from "../lib/format"
import { extraLabel, withExtras } from "../lib/order-items"
import { caps, localized, t, tc } from "../lib/i18n"
import { toolStates, type Tool, type ToolWhy } from "../lib/order-tools"
import { errorMessage, hqApi, medusaAdminUrl, type OrderDetail } from "../lib/medusa"
import { slipsUrl } from "../lib/slip"
import { bookingApi } from "../lib/booking"

/** Where Back goes when there is no page behind: the orders list, named in the current language. */
const backToOrders = () => ({ to: "/orders", label: t("Orders") })

export function OrderPage() {
  const { id = "" } = useParams()
  const order = useQuery({ queryKey: ["hq", "order", id], queryFn: () => hqApi.order(id).then((body) => body.order) })
  // Asked for beside the order, not after it: the notes and the booking need only the order's id, and
  // waiting for the order first added ~100 ms to every order page (same keys as their components).
  useQuery({ queryKey: ["hq", "notes", `site:${id}`], queryFn: () => hqApi.notes(`site:${id}`).then((body) => body.notes), notifyOnChangeProps: [] })
  useQuery({ queryKey: ["booking", "orders", id], queryFn: () => bookingApi.orders([id]), retry: false, notifyOnChangeProps: [] })

  return (
    <div className="flex flex-col gap-y-4">
      {!order.data && <PageHeader title={t("Order")} back={backToOrders()} />}
      {order.isLoading && <Skeleton className="h-64 w-full rounded-lg" />}
      {order.isError && <Text className="text-ui-fg-error">{errorMessage(order.error)}</Text>}
      {order.data && <OrderSheet order={order.data} />}
    </div>
  )
}

function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <Container className="flex flex-col gap-y-3">
      <div className="flex items-center justify-between gap-x-2">
        <Heading level="h2">{title}</Heading>
        {action}
      </div>
      {children}
    </Container>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-x-4 py-1.5">
      <Text size="small" className="text-ui-fg-subtle shrink-0">
        {label}
      </Text>
      <div className="txt-compact-small min-w-0 text-right break-words">{children}</div>
    </div>
  )
}

const SITE_MESSAGE_LABEL: Record<SiteMessageKind, string> = localized(
  { box_now_preparing: "BOX NOW, being prepared", box_now_shipped: "BOX NOW, sent", arrange: "Meet-up / home, to arrange", delivered: "Delivered — thank you" },
  "site message"
)

/** A ready Instagram message for where the order is now (lib/site-message.ts), in the owners' wording. */
function SiteMessage({ order }: { order: OrderDetail }) {
  const parts = siteMessage(order)
  if (!parts) return null
  return <MessageBox mkey={parts.key} vars={parts.vars} label={SITE_MESSAGE_LABEL[parts.kind]} handle={order.instagram} />
}

function OrderSheet({ order }: { order: OrderDetail }) {
  const [removing, setRemoving] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [changingDelivery, setChangingDelivery] = useState(false)
  const addressLine = [order.address.line, order.address.city, order.address.postcode].filter(Boolean).join(", ")

  return (
    <>
      <PageHeader
        code
        title={order.ref}
        eyebrow={t("Site order")}
        back={backToOrders()}
        actions={<span className="hc-data text-[20px] leading-tight lg:text-[22px]">{money(order.total)}</span>}
      >
        <Text size="small" className="text-ui-fg-subtle -mt-1">
          {order.customer} · {dateTime(order.created_at)}
        </Text>
        <div className="flex flex-wrap gap-1.5">
          <DeliveryBadge delivery={order.delivery} />
          <PaymentBadge payment={order.payment} />
          <FulfilmentBadge order={order} />
        </div>
      </PageHeader>

      <OrderTools order={order} />

      <Actions order={order} />

      {(order.delivery === "meetup" || order.delivery === "home") && order.status !== "canceled" && <BookDeliveryPanel order={order} />}

      <SiteMessage order={order} />

      <Section title={t("Customer")}>
        <div className="divide-ui-border-base flex flex-col divide-y">
          <Row label={t("Name")}>{order.customer}</Row>
          {order.phone && (
            <Row label={t("Phone")}>
              <a href={`tel:${order.phone}`} className="text-ui-fg-interactive">{order.phone}</a>
            </Row>
          )}
          {order.email && (
            <Row label={t("Email")}>
              <a href={`mailto:${order.email}`} className="text-ui-fg-interactive break-all">{order.email}</a>
            </Row>
          )}
          {order.instagram && (
            <Row label="Instagram">
              <a href={`https://ig.me/m/${encodeURIComponent(order.instagram)}`} target="_blank" rel="noreferrer" className="text-ui-fg-interactive">
                {t("@{handle} · message", { handle: order.instagram })}
              </a>
            </Row>
          )}
        </div>
      </Section>

      <Section title={t("Delivery")}>
        {order.delivery === "box_now" && <BoxNowCopy lines={order.boxnow} />}
        <div className="divide-ui-border-base flex flex-col divide-y">
          <Row label={t("Method")}>{DELIVERY_LABEL[order.delivery]}</Row>
          {addressLine && order.delivery !== "box_now" && (
            <Row label={order.delivery === "meetup" ? t("Meet-up") : t("Address")}>
              <span className="inline-flex items-center gap-x-1">{addressLine} <CopyButton content={addressLine} /></span>
            </Row>
          )}
          {order.fulfillments.filter((f) => !f.canceled_at).map((fulfillment) => (
            <Row key={fulfillment.id} label={fulfillment.delivered_at ? tc("order", "Delivered") : fulfillment.shipped_at ? tc("order", "Shipped") : tc("order", "Fulfilled")}>
              {dateTime(fulfillment.delivered_at ?? fulfillment.shipped_at ?? fulfillment.created_at)}
              {fulfillment.tracking.map((label) => (
                <div key={label.number}>
                  {label.url ? <a href={label.url} target="_blank" rel="noreferrer" className="text-ui-fg-interactive">{label.number}</a> : label.number}
                </div>
              ))}
            </Row>
          ))}
        </div>
      </Section>

      <Section
        title={t("Items ({n})", { n: order.units })}
        action={
          <Button variant="secondary" size="small" asChild>
            <Link to={slipsUrl([order.id])}><DocumentText /> {t("Packing slip")}</Link>
          </Button>
        }
      >
        <ul className="flex flex-col gap-y-3">
          {withExtras(order.items).map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-x-3">
              <div className="bg-ui-bg-subtle size-12 shrink-0 overflow-hidden rounded-md">
                {item.thumbnail && <img src={item.thumbnail} alt="" className="size-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <Text size="small" weight="plus" className={clx("truncate", item.quantity === 0 && "text-ui-fg-muted line-through")}>{item.title}</Text>
                <Text size="xsmall" className="text-ui-fg-subtle">
                  {[item.variant && item.variant !== "Default Title" ? item.variant : null, `× ${item.quantity}`].filter(Boolean).join(" · ")}
                </Text>
                {item.quantity === 0 ? (
                  <Badge size="2xsmall" color="red">{t("Out of stock: taken out")}</Badge>
                ) : (
                  order.status !== "canceled" &&
                  item.quantity > item.fulfilled &&
                  removing !== item.id && (
                    <button type="button" className="txt-compact-xsmall text-ui-fg-interactive" onClick={() => setRemoving(item.id)}>{t("Change or remove")}</button>
                  )
                )}
              </div>
              <span className="hc-data shrink-0 text-[13px]">{money(item.unit_price * item.quantity)}</span>
              {removing === item.id && <RemoveItem order={order} item={item} onClose={() => setRemoving(null)} />}
              {/* The packaging (or another extra) chosen for this item, under it. */}
              {item.extras.map((extra) => (
                <div key={extra.id} className="flex w-full items-baseline justify-between gap-x-3 pl-[60px]">
                  <Text size="xsmall" className="text-ui-fg-subtle min-w-0">{extraLabel(extra)}{extra.quantity > 1 ? ` × ${extra.quantity}` : ""}</Text>
                  <span className="hc-data shrink-0 text-[12px] text-ui-fg-subtle">{money(extra.unit_price * extra.quantity)}</span>
                </div>
              ))}
            </li>
          ))}
        </ul>
        {order.status !== "canceled" && (adding ? (
          <AddOrderItem order={order} onClose={() => setAdding(false)} />
        ) : (
          <button type="button" className="txt-compact-small-plus text-ui-fg-interactive self-start" onClick={() => setAdding(true)}>+ {t("Add a product")}</button>
        ))}
        {(order.removed ?? []).length > 0 && (
          <ul className="flex flex-col gap-y-1">
            {(order.removed ?? []).map((line, index) => (
              <li key={index} className="flex flex-wrap items-baseline justify-between gap-x-3">
                <Text size="small" className="text-ui-fg-muted">
                  <span className="line-through">{line.title}{line.units > 1 ? ` × ${line.units}` : ""}</span>
                  {line.to ? ` → ${line.to}` : ""}
                </Text>
                <Badge size="2xsmall" color={line.to ? "blue" : "red"}>{line.to ? t("Out of stock: changed") : t("Out of stock: taken out")}</Badge>
              </li>
            ))}
          </ul>
        )}
        <div className="border-ui-border-base flex flex-col border-t pt-2">
          {order.shipping.map((method, index) => (
            <Row key={index} label={method.name ?? t("Shipping")}>
              <span className="inline-flex items-baseline gap-x-2">
                {order.status !== "canceled" && order.shipping.length === 1 && !changingDelivery && (
                  <button type="button" className="txt-compact-xsmall text-ui-fg-interactive" onClick={() => setChangingDelivery(true)}>{t("Change")}</button>
                )}
                {money(method.amount)}
              </span>
            </Row>
          ))}
          {changingDelivery && order.shipping.length === 1 && (
            <DeliveryCharge order={order} current={order.shipping[0].amount} onClose={() => setChangingDelivery(false)} />
          )}
          {(order.delivery_changes ?? []).map((change, index) => (
            <Text key={index} size="xsmall" className="text-ui-fg-muted">
              {t("Delivery charge changed from {from} to {to} on {date}", { from: money(change.from), to: money(change.to), date: dateTime(change.at) })}
              {change.note ? ` · ${change.note}` : ""}
            </Text>
          ))}
          {order.discount.total > 0 && (
            <Row label={order.discount.codes.length ? t("Discount {codes}", { codes: order.discount.codes.join(", ") }) : t("Discount")}>− {money(order.discount.total)}</Row>
          )}
          {order.payment.refunded > 0 && <Row label={t("Refunded")}>− {money(order.payment.refunded)}</Row>}
          <Row label={t("Total")}><strong className="hc-data">{money(order.total)}</strong></Row>
        </div>
      </Section>

      <OrderNotes orderKey={`site:${order.id}`} />

      <ReturnPanel order={order} />

      <RefundPanel order={order} />

      <OrderEmails order={order} />

      <div className="flex flex-wrap gap-2 pb-2">
        <Button variant="secondary" size="small" asChild>
          <a href={medusaAdminUrl(`/orders/${order.id}`)} target="_blank" rel="noreferrer">
            <ArrowUpRightOnBox /> {t("Open in Medusa admin")}
          </a>
        </Button>
      </div>
    </>
  )
}

/** Older than this, an open order is almost certainly backlog or a test: default to not emailing. */
const QUIET_AFTER_DAYS = 7

const ageOf = (order: OrderDetail) => (order.created_at ? (Date.now() - new Date(order.created_at).getTime()) / 86_400_000 : 0)

/** Why the email box starts unticked on an old order. */
const quietReason = (ageDays: number) => t.n(Math.floor(ageDays), "Off because the order is {n} day old.", "Off because the order is {n} days old.")

/** The order's next step — ship it, hand it over, record the cash — as the first card on the page. */
function Actions({ order }: { order: OrderDetail }) {
  const queryClient = useQueryClient()
  const prompt = usePrompt()
  const [voucher, setVoucher] = useState("")
  const [withCash, setWithCash] = useState(order.payment.can_collect_cash)
  const ageDays = ageOf(order)
  const [notify, setNotify] = useState(ageDays < QUIET_AFTER_DAYS)

  const done = (next: OrderDetail, message: string) => {
    queryClient.setQueryData(["hq", "order", order.id], next)
    void queryClient.invalidateQueries({ queryKey: ["hq", "orders"] })
    toast.success(message)
  }
  const onError = (error: unknown) => toast.error(errorMessage(error))

  const ship = useMutation({
    mutationFn: (number: string) => hqApi.ship(order.id, number, notify),
    onSuccess: (body) =>
      done(body.order, body.result.already_shipped ? t("Already shipped with that voucher.") : notify ? t("Shipped. Tracking email on its way.") : t("Shipped.")),
    onError,
  })
  const handOver = useMutation({
    mutationFn: (collectCash: boolean) => hqApi.handOver(order.id, collectCash, notify),
    onSuccess: (body) => done(body.order, notify ? t("Handed over. Thank-you email on its way.") : t("Handed over.")),
    onError,
  })
  const collect = useMutation({
    mutationFn: () => hqApi.collectCash(order.id),
    onSuccess: (body) => done(body.order, t("Cash recorded.")),
    onError,
  })

  async function submitShip(event: FormEvent) {
    event.preventDefault()
    const number = voucher.trim()
    if (!number) return
    const ok = await prompt({
      title: t("Fulfil {ref} with BOX NOW?", { ref: order.ref }),
      description: notify
        ? t("Tracking number {number}. The order is marked shipped. {name} is emailed the tracking number and the BOX NOW link.", { number, name: order.customer })
        : t("Tracking number {number}. The order is marked shipped. No email is sent.", { number }),
      confirmText: t("Fulfil"),
      cancelText: t("Back"),
      variant: "confirmation",
    })
    if (ok) ship.mutate(number)
  }

  async function confirmHandOver() {
    const name = order.customer
    const ok = await prompt({
      title: t("Handed over {ref}?", { ref: order.ref }),
      description: withCash
        ? notify
          ? t("The order is marked delivered and the cash recorded. {name} is emailed a thank-you.", { name })
          : t("The order is marked delivered and the cash recorded. No email is sent.")
        : notify
          ? t("The order is marked delivered. {name} is emailed a thank-you.", { name })
          : t("The order is marked delivered. No email is sent."),
      confirmText: t("Handed over"),
      cancelText: t("Back"),
      variant: "confirmation",
    })
    if (ok) handOver.mutate(withCash)
  }

  // An exchange recorded on a return, whose new item has not gone out yet, is the next step too.
  const toSend = (order.returns ?? []).filter((entry) => entry.outcome === "exchange" && entry.exchange_status === "to_send")
  const boxNowShip = order.delivery === "box_now" && order.can_ship
  const handOverNow = order.delivery !== "box_now" && order.can_hand_over
  const cashNow = order.payment.can_collect_cash && (order.delivered || order.delivery === "box_now")

  if (order.status === "canceled" || (!boxNowShip && !handOverNow && !cashNow && !toSend.length)) {
    return (
      <Container className="flex items-center gap-x-2">
        <CheckCircleSolid className={order.status === "canceled" ? "text-ui-fg-muted" : "text-ui-tag-green-icon"} />
        <Text size="small">{order.status === "canceled" ? t("This order is cancelled.") : t("Nothing left to do for this order.")}</Text>
      </Container>
    )
  }

  return (
    <Container className="border-ui-fg-base flex flex-col gap-y-4 border-l-[3px]">
      <p className="hc-eyebrow -mb-2">{caps(t("Next step"))}</p>
      {toSend.map((entry) => <ExchangeSend key={entry.id} order={order} entry={entry} />)}
      {boxNowShip && (
        <form onSubmit={submitShip} className="flex flex-col gap-y-2.5">
          <Heading level="h2">{t("Fulfil with BOX NOW")}</Heading>
          <Text size="small" className="text-ui-fg-subtle">
            {t("Make the voucher in the BOX NOW portal (the four lines under Delivery), then paste its tracking number.")}
          </Text>
          <ul className="flex flex-col gap-y-1">
            {withExtras(order.items.filter((item) => item.quantity > 0)).map((item) => (
              <li key={item.id} className="flex flex-wrap items-baseline justify-between gap-x-2">
                <Text size="small" className="min-w-0 truncate">{item.title}{item.variant && item.variant !== "Default Title" ? ` · ${item.variant}` : ""}</Text>
                <Text size="small" className="text-ui-fg-subtle shrink-0">× {item.quantity - item.fulfilled || item.quantity}</Text>
                {item.extras.map((extra) => <Text key={extra.id} size="xsmall" className="text-ui-fg-subtle w-full pl-3">{extraLabel(extra)}</Text>)}
              </li>
            ))}
          </ul>
          <Input
            id="voucher"
            inputMode="numeric"
            autoComplete="off"
            placeholder={t("Tracking number")}
            aria-label={t("BOX NOW tracking number")}
            value={voucher}
            onChange={(event) => setVoucher(event.target.value)}
          />
          <div className="flex items-start gap-x-2">
            <Checkbox id="notify-ship" checked={notify} onCheckedChange={(value) => setNotify(value === true)} className="mt-0.5" />
            <Label htmlFor="notify-ship" className="txt-compact-small">
              {order.email != null
                ? t("Email {email} that it is on its way, with the tracking number and the BOX NOW link", { email: order.email })
                : t("Email the customer that it is on its way, with the tracking number and the BOX NOW link")}
            </Label>
          </div>
          <Button type="submit" isLoading={ship.isPending} disabled={!voucher.trim()} className="w-full">{t("Fulfil")}</Button>
        </form>
      )}

      {handOverNow && (
        <div className="flex flex-col gap-y-2.5">
          <Heading level="h2">{order.delivery === "meetup" ? t("Hand over at the meet-up") : t("Hand over at home")}</Heading>
          {order.payment.can_collect_cash && (
            <div className="flex items-center gap-x-2">
              <Checkbox id="with-cash" checked={withCash} onCheckedChange={(value) => setWithCash(value === true)} />
              <Label htmlFor="with-cash">{t("Cash collected too ({amount})", { amount: money(order.total) })}</Label>
            </div>
          )}
          <div className="flex items-start gap-x-2">
            <Checkbox id="notify" checked={notify} onCheckedChange={(value) => setNotify(value === true)} className="mt-0.5" />
            <div>
              <Label htmlFor="notify">{t("Email the customer a thank-you")}</Label>
              {!notify && ageDays >= QUIET_AFTER_DAYS && (
                <Text size="xsmall" className="text-ui-fg-muted">{quietReason(ageDays)}</Text>
              )}
            </div>
          </div>
          <Button className="w-full" onClick={() => void confirmHandOver()} isLoading={handOver.isPending}>{t("Handed over")}</Button>
        </div>
      )}

      {cashNow && (
        <div className="flex items-center justify-between gap-x-3">
          <Text size="small">{t("Cash of {amount} not recorded yet.", { amount: money(order.total) })}</Text>
          <Button variant="secondary" onClick={() => collect.mutate()} isLoading={collect.isPending}>{t("Cash collected")}</Button>
        </div>
      )}
    </Container>
  )
}

const TOOL_LABEL: Record<Tool, string> = localized({ refund: "Refund", return: "Return", cancel: "Cancel" }, "order tool")
const TOOL_WHY: Record<ToolWhy, string> = localized(
  {
    all_refunded: "All refunded",
    cash_not_collected: "Cash not taken yet",
    nothing_taken: "No money taken",
    canceled: "Cancelled",
    not_left: "Nothing sent yet",
    all_back: "Everything is back",
    has_left: "Sent: use Return",
  },
  "order tool"
)

/**
 * Refund, Return and Cancel, always in one row under the order's header (owners, 9 Oct 2026: easy
 * to find, easy to use). A tool that cannot be used says why under its button; the chosen one opens
 * its form right here.
 */
function OrderTools({ order }: { order: OrderDetail }) {
  const [tool, setTool] = useState<Tool | null>(null)
  const states = toolStates(order)
  const open = tool && states[tool].ok ? tool : null
  return (
    <Container className="flex flex-col gap-y-3">
      <div className="grid grid-cols-3 gap-2" role="group" aria-label={t("Refund, return or cancel")}>
        {(["refund", "return", "cancel"] as const).map((key) => {
          const state = states[key]
          return (
            <div key={key} className="flex min-w-0 flex-col items-stretch gap-y-1">
              <Button
                size="small"
                className="hc-wrap w-full"
                variant={open === key ? "primary" : key === "cancel" && state.ok ? "danger" : "secondary"}
                disabled={!state.ok}
                aria-pressed={open === key}
                onClick={() => setTool(open === key ? null : key)}
              >
                {TOOL_LABEL[key]}
              </Button>
              {!state.ok && <Text size="xsmall" className="text-ui-fg-muted text-center leading-tight">{TOOL_WHY[state.why]}</Text>}
            </div>
          )
        })}
      </div>
      {open && (
        <div className="border-ui-border-base border-t pt-3">
          {open === "refund" && <RefundForm order={order} onClose={() => setTool(null)} />}
          {open === "return" && <ReturnForm order={order} onClose={() => setTool(null)} />}
          {open === "cancel" && <CancelForm order={order} onClose={() => setTool(null)} />}
        </div>
      )}
    </Container>
  )
}

/**
 * Cancel in one step, for an order that has not left the shop: the money taken goes back (a card
 * through Stripe; cash recorded, handed back by you), and the items go back on sale.
 */
function CancelForm({ order, onClose }: { order: OrderDetail; onClose: () => void }) {
  const queryClient = useQueryClient()
  const prompt = usePrompt()
  const ageDays = ageOf(order)
  const [notify, setNotify] = useState(ageDays < QUIET_AFTER_DAYS)
  const back = order.payment.refundable
  const card = order.payment.kind === "card"
  const moneyLine =
    back > 0
      ? card
        ? t("{amount} goes back to the customer's card through Stripe. This is real money and cannot be undone.", { amount: money(back) })
        : t("The refund is recorded. Give the {amount} back in cash yourself.", { amount: money(back) })
      : t("No money moves.")
  const cancel = useMutation({
    mutationFn: () => hqApi.cancel(order.id, notify),
    onSuccess: (body) => {
      queryClient.setQueryData(["hq", "order", order.id], body.order)
      void queryClient.invalidateQueries({ queryKey: ["hq", "orders"] })
      toast.success(
        [
          t("Order cancelled."),
          back > 0 ? (card ? t("{amount} refunded to the card.", { amount: money(back) }) : t("{amount} refunded (recorded).", { amount: money(back) })) : null,
          notify ? t("The customer is being emailed.") : null,
        ]
          .filter(Boolean)
          .join(" ")
      )
      onClose()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  async function confirmCancel() {
    const ok = await prompt({
      title: t("Cancel {ref}?", { ref: order.ref }),
      description: [
        t("The order is cancelled and its items go back on sale."),
        moneyLine,
        notify ? t("{name} is emailed that it was cancelled.", { name: order.customer }) : t("No email is sent."),
        t("This cannot be undone."),
      ].join(" "),
      confirmText: t("Cancel order"),
      cancelText: t("Keep order"),
      variant: "danger",
    })
    if (ok) cancel.mutate()
  }

  return (
    <div className="flex flex-col gap-y-2.5">
      <ul className="flex flex-col gap-y-1">
        <li><Text size="small">{t("The items go back on sale.")}</Text></li>
        <li><Text size="small" className={clx(back > 0 && "font-medium")}>{moneyLine}</Text></li>
      </ul>
      <div className="flex items-start gap-x-2">
        <Checkbox id="notify-cancel" checked={notify} onCheckedChange={(value) => setNotify(value === true)} className="mt-0.5" />
        <div>
          <Label htmlFor="notify-cancel">{t("Email the customer")}</Label>
          {!notify && ageDays >= QUIET_AFTER_DAYS && <Text size="xsmall" className="text-ui-fg-muted">{quietReason(ageDays)}</Text>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="danger" size="small" onClick={() => void confirmCancel()} isLoading={cancel.isPending}>
          {back > 0 ? t("Cancel and refund {amount}", { amount: money(back) }) : t("Cancel order")}
        </Button>
        <Button size="small" variant="secondary" onClick={onClose} disabled={cancel.isPending}>{t("Keep order")}</Button>
      </div>
    </div>
  )
}
